import type { NetworkInterface } from "./api/client";
import { useCallback, useEffect, useState, useRef, useId, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ApiError } from "./api/client";

/**
 * useAsync runs a fetch on mount and gives back the three states every view
 * here needs to render honestly: loading, failed (with the server's own
 * message), and loaded. `reload` re-runs it after a write.
 */
export function useAsync<T>(fn: (signal: AbortSignal) => Promise<T>, deps: unknown[] = [], refreshMs = 0) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  useEffect(() => {
    if (!refreshMs) return;
    const timer = window.setInterval(() => { if (!document.hidden) setNonce(n => n + 1); }, refreshMs);
    return () => window.clearInterval(timer);
  }, [refreshMs]);

  useEffect(() => {
    const ac = new AbortController();
    let live = true;
    setLoading(true);
    fn(ac.signal)
      .then((d) => {
        if (live) {
          setData(d);
          setUpdatedAt(new Date());
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!live || ac.signal.aborted) return;
        // A 401 is already being handled by the shell (it redirects to
        // sign-in); showing "unauthorized" under it just adds noise.
        if (e instanceof ApiError && e.isAuth) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload, updatedAt };
}

export function Page({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <div className="page-head">
        <h1>{title}</h1>
        {actions ? <div className="row">{actions}</div> : null}
      </div>
      {subtitle ? <p className="page-sub">{subtitle}</p> : null}
      {children}
    </>
  );
}

/** Renders the loading / error / empty states so each view does not restate them. */
export function Async<T>({
  state,
  empty,
  children,
}: {
  state: { data: T | null; error: string | null; loading: boolean };
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (state.error) return <div className="banner error">{state.error}</div>;
  if (state.loading && state.data === null) return <div className="empty">Loading…</div>;
  if (state.data === null) return <div className="empty">{empty ?? "Nothing to show."}</div>;
  if (Array.isArray(state.data) && state.data.length === 0) {
    return <div className="empty">{empty ?? "Nothing here yet."}</div>;
  }
  return <>{children(state.data)}</>;
}

export function Card({ children }: { children: ReactNode }) {
  return <div className="card">{children}</div>;
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: "good" | "warn" | "crit" }) {
  return (
    <div className="card stat">
      <div className="label">{label}</div>
      <div className="value" style={tone ? { color: `var(--${tone})` } : undefined}>
        {value}
      </div>
    </div>
  );
}

export function Table({ head, children, className = "" }: { head: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`card table-wrap ${className}`} style={{ padding: 0 }}>
      <table>
        <thead>
          <tr>{head}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /**
   * For dialogs carrying a table or several columns of fields. The default is
   * deliberately narrow — a confirmation stretched across a desktop monitor is
   * harder to read, not easier — so this is opt-in per dialog rather than a
   * width every modal grows into.
   */
  wide?: boolean;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const node = dialog.current!;
    const focusable = () => Array.from(node.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]'));
    (focusable()[0] ?? node).focus();
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); close.current(); }
      if (e.key !== "Tab") return;
      const items = focusable();
      const first = items[0] ?? node;
      const last = items[items.length - 1] ?? node;
      if (e.shiftKey && (document.activeElement === first || !node.contains(document.activeElement))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !node.contains(document.activeElement))) {
        e.preventDefault(); first.focus();
      }
    };
    const onFocus = (e: FocusEvent) => { if (!node.contains(e.target as Node)) (focusable()[0] ?? node).focus(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("focusin", onFocus);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", onFocus);
      document.body.style.overflow = oldOverflow;
      previous?.focus();
    };
  }, []);
  // Rendered into <body> rather than where it was called from.
  //
  // The backdrop is position: fixed, which anchors to the viewport only while
  // no ancestor establishes a containing block — a transform, filter,
  // will-change or contain on any parent silently re-anchors it to that parent
  // instead, and the dialog then sizes and positions against a box that is not
  // the screen. Nothing in this stylesheet does that to a modal's ancestors
  // today, but the dialog is mounted from deep inside whichever view opened it,
  // and a style added to a panel months from now should not be able to make a
  // dialog unreachable. A portal takes the whole class of failure off the table.
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div ref={dialog} className={wide ? "modal wide" : "modal"} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <h2 id={titleId}>{title}</h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  hint,
  id,
  problem,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
  /** Overrides the generated id, so a caller can address the input by name. */
  id?: string;
  /**
   * What is wrong with the current value. Replaces the hint rather than
   * stacking under it: the hint says what the field wants, the problem says
   * why this value isn't it, and showing both makes the operator read twice to
   * find the part that changed.
   */
  problem?: string;
}) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <>
      <label htmlFor={fieldId}>{label}</label>
      <input
        id={fieldId}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={problem ? "invalid" : undefined}
        aria-invalid={problem ? true : undefined}
        aria-describedby={problem ? `${fieldId}-problem` : undefined}
      />
      {problem ? (
        <div className="err" id={`${fieldId}-problem`} style={{ marginTop: 4 }}>{problem}</div>
      ) : hint ? (
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{hint}</div>
      ) : null}
    </>
  );
}

/**
 * A tab strip whose selection lives in the URL query string.
 *
 * In the query rather than in component state so that a tab survives a reload
 * and can be linked to — "look at the impairments on branch-01" should be a URL,
 * not an instruction to click twice.
 */
export function Tabs({
  tabs,
  active,
  onSelect,
}: {
  tabs: { id: string; label: string; badge?: number }[];
  active: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={t.id === active}
          className={t.id === active ? "tab active" : "tab"}
          onClick={() => onSelect(t.id)}
        >
          {t.label}
          {t.badge ? <span className="tab-badge">{t.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

/**
 * A picker over an agent's real interfaces.
 *
 * These are reported on every heartbeat and stored, so asking an operator to
 * type `eth1` into a box that accepts anything only ever produced impairments
 * and scenarios that silently targeted nothing. The agent's own view is the
 * authority on what it has.
 *
 * It falls back to a free-text input when the agent has never reported —
 * a brand new one, or one that is down — because refusing to act on an
 * unreachable agent would be worse than letting the operator name the
 * interface themselves.
 */
export function InterfacePicker({
  interfaces,
  value,
  onChange,
  label = "Interface",
}: {
  interfaces: NetworkInterface[] | undefined;
  value: string;
  onChange: (v: string) => void;
  label?: string;
}) {
  const list = interfaces ?? [];
  // The label names the select for assistive technology and for a click on
  // it; a bare <label> beside a control does neither.
  const id = useId();
  // Keep the selection on something that exists. The agent can change under
  // this control (the form picks an agent first), and a select whose value
  // matches no option renders blank while still reporting that value — which
  // would submit an interface the agent does not have.
  const selected = list.some((i) => i.name === value) ? value : (list[0]?.name ?? "");
  useEffect(() => {
    if (selected !== value) onChange(selected);
  }, [selected, value, onChange]);

  if (list.length === 0) {
    return (
      <Field
        label={label}
        value={value}
        onChange={onChange}
        placeholder="eth0"
        hint="This shard has not reported its interfaces yet, so they cannot be listed. Check the name before applying."
      />
    );
  }
  return (
    <>
      <label htmlFor={id}>{label}</label>
      <select id={id} value={selected} onChange={(e) => onChange(e.target.value)}>
        {list.map((i) => (
          <option key={i.name} value={i.name}>
            {i.name}
            {i.addresses?.length ? ` — ${i.addresses.join(", ")}` : ""}
            {i.up ? "" : " (down)"}
          </option>
        ))}
      </select>
    </>
  );
}

/** Absolute time, with a plain dash for the many nullable timestamps. */
/**
 * A timestamp as a person reads one in a table: "Oct 8, 12:11 PM", with the
 * year only when it is not this one. Seconds are for the places that need
 * them (event times, failover timing), which format their own.
 */
export function when(t: string | null | undefined): string {
  if (!t) return "—";
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return "—";
  const thisYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString([], {
    year: thisYear ? undefined : "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export function relative(t: string | null | undefined): string {
  if (!t) return "never";
  const ms = Date.now() - new Date(t).getTime();
  if (Number.isNaN(ms)) return "never";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function Pill({ tone, children }: { tone?: "good" | "warn" | "crit"; children: ReactNode }) {
  return <span className={tone ? `pill ${tone}` : "pill"}>{children}</span>;
}

/**
 * What useAsync hands back, named so a component can take one as a prop.
 *
 * Panels that draw a fetch's result need to accept the whole thing — data for
 * the markup, reload for the buttons, and loading/error for <Async> — and
 * without a name for it every such prop was an inline structural type written
 * out again at each call site.
 */
export type AsyncState<T> = ReturnType<typeof useAsync<T>>;

/**
 * The configuration gear.
 *
 * Inline SVG rather than an icon font or a dependency: it is one shape, it has
 * to inherit the button's colour in both themes, and a webfont for a single
 * glyph is a network request that can fail and leave a blank square where the
 * only route to an agent's settings should be.
 */
export function GearIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
